import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { FaSpinner } from 'react-icons/fa';
import projectService from '../../services/projectService';
import '../FileManager/ImageViewerModal.css';

const TaskAttachmentImageViewerModal = ({ taskId, attachment, allImageAttachments = [], onClose }) => {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                setLoading(true);
                setError(null);

                const imagesToLoad = allImageAttachments.length > 0 ? allImageAttachments : (attachment ? [attachment] : []);

                if (imagesToLoad.length === 0) {
                    setError('No file provided');
                    setLoading(false);
                    return;
                }

                const imageSlides = await Promise.all(
                    imagesToLoad.map(async (img) => {
                        try {
                            const res = await projectService.getAttachmentViewUrl(taskId, img.id);
                            const name = img.upload?.original_filename || img.upload?.name || 'Image';
                            return {
                                src: res?.status && res?.data?.url ? res.data.url : null,
                                title: name,
                                alt: name,
                            };
                        } catch (err) {
                            const name = img.upload?.original_filename || img.upload?.name || 'Image';
                            return { src: null, title: name };
                        }
                    })
                );

                const validSlides = imageSlides.filter(s => s.src !== null);

                if (validSlides.length === 0) {
                    setError('No images available');
                } else {
                    setSlides(validSlides);
                    const targetName = attachment?.upload?.original_filename || attachment?.upload?.name;
                    const idx = validSlides.findIndex(s => s.alt === targetName);
                    setCurrentIndex(idx >= 0 ? idx : 0);
                }
            } catch (err) {
                setError(err.message || 'Failed to load image');
            } finally {
                setLoading(false);
            }
        };

        fetchImages();
    }, [taskId, attachment, allImageAttachments]);

    if (!attachment && !loading) return null;

    const handleClose = () => {
        setSlides([]);
        onClose();
    };

    return (
        <>
            {loading && (
                <div className="image-viewer-overlay" onClick={handleClose}>
                    <div className="image-viewer-loading">
                        <FaSpinner className="image-viewer-spinner" />
                        <p>Loading image...</p>
                    </div>
                </div>
            )}
            {error && !loading && (
                <div className="image-viewer-overlay" onClick={handleClose}>
                    <div className="image-viewer-error">
                        <p>{error}</p>
                        <button onClick={handleClose}>Close</button>
                    </div>
                </div>
            )}
            {!loading && !error && slides.length > 0 && (
                <Lightbox
                    open={true}
                    close={() => handleClose()}
                    slides={slides}
                    index={currentIndex}
                    on={{
                        view: ({ index }) => setCurrentIndex(index)
                    }}
                    plugins={[Zoom]}
                    animation={{ fade: 250, swipe: 250 }}
                    carousel={{ finite: true }}
                    controller={{
                        closeOnBackdropClick: true,
                    }}
                    styles={{
                        container: { backgroundColor: 'rgba(0,0,0,0.95)' }
                    }}
                    zoom={{
                        maxZoomPixelRatio: 3,
                        zoomInMultiplier: 2,
                        doubleClickMaxStops: 2,
                        keyboardMoveDistance: 50,
                        wheelZoomDistanceFactor: 100,
                    }}
                />
            )}
        </>
    );
};

export default TaskAttachmentImageViewerModal;
